import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuoteUploadPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState({ valid: false, errors: [] });

  const validateExcel = async (data) => {
    const errors = [];
    const requiredColumns = [
      'Quote No',
      'Quote Date',
      'Fault Number',
      'Supervisor Name',
      'Work Description',
      'Store Address',
      'Mail Approval Date',
      'AFM Name',
      'Amount With Management Fee'
    ];

    if (!data || data.length === 0) {
      errors.push('Excel file is empty');
      return { valid: false, errors };
    }

    const headers = Object.keys(data[0]);
    const missingColumns = requiredColumns.filter(col => !headers.includes(col));

    if (missingColumns.length > 0) {
      errors.push(`Missing columns: ${missingColumns.join(', ')}`);
    }

    // Check for duplicates within file
    const quoteNumbers = [];
    const seenQuoteNumbers = new Set();
    data.forEach((row, index) => {
      const quoteNo = row['Quote No'];
      if (!quoteNo) {
        errors.push(`Row ${index + 2}: Missing Quote No`);
      } else {
        const quoteNoStr = String(quoteNo);
        if (seenQuoteNumbers.has(quoteNoStr)) {
          errors.push(`Duplicate Quote No found in file: ${quoteNoStr}`);
        } else {
          seenQuoteNumbers.add(quoteNoStr);
          quoteNumbers.push(quoteNoStr);
        }
      }

      if (!row['Supervisor Name']) {
        errors.push(`Row ${index + 2}: Missing Supervisor Name`);
      }

      if (!row['Amount With Management Fee'] || isNaN(row['Amount With Management Fee'])) {
        errors.push(`Row ${index + 2}: Invalid or missing Amount`);
      }
    });

    // Check for existing quote numbers in database
    if (quoteNumbers.length > 0 && errors.length === 0) {
      try {
        const response = await axios.post(`${API}/quotes/check-duplicates`, quoteNumbers);
        const duplicates = response.data.duplicates || [];
        
        duplicates.forEach(dup => {
          errors.push(dup.message);
        });
      } catch (error) {
        console.error('Error checking duplicates:', error);
      }
    }

    return { valid: errors.length === 0, errors };
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setValidating(true);
    setValidation({ valid: false, errors: [] });

    // Read and preview Excel
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);

        setPreview(jsonData.slice(0, 5)); // Preview first 5 rows

        const validationResult = await validateExcel(jsonData);
        setValidation(validationResult);
        setValidating(false);

        if (validationResult.valid) {
          toast.success('Excel validation passed!');
        } else {
          toast.error('Excel validation failed. Check errors below.');
        }
      } catch (error) {
        toast.error('Failed to parse Excel file');
        console.error(error);
        setValidating(false);
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });

  const handleUpload = async () => {
    if (!file || !validation.valid) {
      toast.error('Please select a valid Excel file');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/quotes/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { message, quotes_created, warnings } = response.data;
      
      // Show success message if quotes were created
      if (quotes_created && quotes_created.length > 0) {
        toast.success(message);
        
        // Also show warnings for skipped duplicates
        if (warnings && warnings.length > 0) {
          warnings.forEach(warning => {
            toast.warning(warning, { duration: 6000 });
          });
        }
        
        navigate('/quotes');
      } else if (warnings && warnings.length > 0) {
        // No quotes created - show each duplicate warning
        warnings.forEach(warning => {
          toast.error(warning, { duration: 6000 });
        });
      } else {
        toast.success(message);
        navigate('/quotes');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload quotes');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Sidebar>
      <div className="space-y-8" data-testid="quote-upload-page">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Upload Quotes
          </h1>
          <p className="text-muted-foreground mt-2">
            Upload an approved quote Excel file with advanced validation
          </p>
        </div>

        {/* Upload Area */}
        <Card className="p-8 glass-card">
          <div
            {...getRootProps()}
            className={
              `border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer ${
                isDragActive ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'
              }`
            }
            data-testid="dropzone"
          >
            <input {...getInputProps()} data-testid="file-input" />
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 bg-accent/10 rounded-full">
                <Upload className="h-10 w-10 text-accent" />
              </div>
              {isDragActive ? (
                <p className="text-lg font-medium">Drop the Excel file here...</p>
              ) : (
                <>
                  <p className="text-lg font-medium">Drag & drop Excel file here</p>
                  <p className="text-sm text-muted-foreground">or click to browse</p>
                  <Badge variant="secondary">Supports .xlsx, .xls</Badge>
                </>
              )}
            </div>
          </div>

          {file && (
            <div className="mt-6 p-4 bg-muted/50 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="h-8 w-8 text-accent" />
                <div>
                  <p className="font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(2)} KB
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => {
                setFile(null);
                setPreview(null);
                setValidation({ valid: false, errors: [] });
              }}>
                Remove
              </Button>
            </div>
          )}
        </Card>

        {/* Validation Results */}
        {file && (
          <Card className="p-6 glass-card">
            <div className="flex items-center gap-3 mb-4">
              {validating ? (
                <div className="h-6 w-6 border-2 border-[#DC143C] border-t-transparent rounded-full animate-spin" />
              ) : validation.valid ? (
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              ) : (
                <AlertCircle className="h-6 w-6 text-red-600" />
              )}
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {validating ? 'Validating...' : validation.valid ? 'Validation Passed' : 'Validation Failed'}
              </h2>
            </div>

            {validation.errors.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-red-600">Errors:</p>
                <ul className="list-disc list-inside space-y-1">
                  {validation.errors.map((error, index) => (
                    <li key={index} className="text-sm text-red-600">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {validation.valid && preview && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">Preview (first 5 rows):</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-2 border text-left">Quote No</th>
                        <th className="p-2 border text-left">Supervisor</th>
                        <th className="p-2 border text-left">Work Description</th>
                        <th className="p-2 border text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((row, index) => (
                        <tr key={index} className="hover:bg-muted/50">
                          <td className="p-2 border font-mono text-xs">{row['Quote No']}</td>
                          <td className="p-2 border">{row['Supervisor Name']}</td>
                          <td className="p-2 border">{row['Work Description']?.substring(0, 50)}...</td>
                          <td className="p-2 border text-right font-mono">
                            ₹{row['Amount With Management Fee']?.toLocaleString('en-IN')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            onClick={handleUpload}
            disabled={!validation.valid || uploading || validating}
            size="lg"
            data-testid="upload-button"
          >
            {uploading ? 'Uploading...' : 'Upload Quotes'}
          </Button>
          <Button variant="outline" onClick={() => navigate('/quotes')} size="lg">
            Cancel
          </Button>
        </div>
      </div>
    </Sidebar>
  );
}
